import { CoreFieldMaps, AdditionalField, Condition, ParseResult } from "./types";

/** Converts a path array like `["device", "gps", "lat"]` to jq syntax: `.device.gps.lat` */
export function jqPath(pathSegments: string[]): string {
    return pathSegments.map(seg =>
        /^\d+$/.test(seg) ? `[${seg}]`       // array index: [0], [1], ...
        : /^\w+$/.test(seg) ? `.${seg}`       // simple key: .name
        : `."${seg}"`                          // special chars: ."frm-payload"
    ).join("");
}

/** Safely traverses a nested object following `pathSegments`. Returns `undefined` if any key is missing. */
export function valAt(obj: any, pathSegments: string[]): any {
    let current = obj;
    for (const key of pathSegments) {
        if (current == null) return undefined;
        current = current[key];
    }
    return current;
}

/** Sets `value` at a dotted path in `obj`, creating intermediate objects as needed. */
function setAtDotted(obj: any, dottedKey: string, value: any): void {
    const segments = dottedKey.split(".");
    let current = obj;
    for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        if (current[seg] == null || typeof current[seg] !== "object") current[seg] = {};
        current = current[seg];
    }
    current[segments[segments.length - 1]] = value;
}

/**
 * Normalizes a jq object body for parsing:
 * - Unquotes object keys that are valid identifiers: `"source":` → `source:`
 */
function sanitizeJqBody(body: string): string {
    return body.replace(/"(\w+)"\s*:/g, "$1:");
}

/**
 * Splits a jq path string into segments, handling quoted keys like `."frm-payload"`.
 * Input: `uplink_message.locations."frm-payload".latitude`
 * Output: `["uplink_message", "locations", "frm-payload", "latitude"]`
 */
function splitJqPath(pathStr: string): string[] {
    const segments: string[] = [];
    let cursor = 0;
    while (cursor < pathStr.length) {
        if (pathStr[cursor] === ".") { cursor++; continue; }
        if (pathStr[cursor] === "[") {
            // Bracket notation: [0], ["key"]
            const closeBracket = pathStr.indexOf("]", cursor + 1);
            if (closeBracket === -1) break;
            let inner = pathStr.slice(cursor + 1, closeBracket);
            // Strip quotes if present: ["key"] → key
            if (inner.startsWith('"') && inner.endsWith('"')) inner = inner.slice(1, -1);
            segments.push(inner);
            cursor = closeBracket + 1;
        } else if (pathStr[cursor] === '"') {
            const closeQuote = pathStr.indexOf('"', cursor + 1);
            if (closeQuote === -1) break;
            segments.push(pathStr.slice(cursor + 1, closeQuote));
            cursor = closeQuote + 1;
        } else {
            let end = cursor;
            while (end < pathStr.length && pathStr[end] !== "." && pathStr[end] !== '"' && pathStr[end] !== "[") end++;
            segments.push(pathStr.slice(cursor, end));
            cursor = end;
        }
    }
    return segments;
}

/** Recursively flattens a nested object/array into an array of `{ path, value }` entries for every key (including intermediate objects and array items). */
export function flatPaths(obj: any, prefix: string[] = []): { path: string[]; value: any }[] {
    const result: { path: string[]; value: any }[] = [];
    if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
            const fullPath = [...prefix, String(index)];
            result.push({ path: fullPath, value: item });
            if (item && typeof item === "object") {
                result.push(...flatPaths(item, fullPath));
            }
        });
    } else if (obj && typeof obj === "object") {
        for (const [key, value] of Object.entries(obj)) {
            const fullPath = [...prefix, key];
            result.push({ path: fullPath, value });
            if (value && typeof value === "object") {
                result.push(...flatPaths(value, fullPath));
            }
        }
    }
    return result;
}

/** Returns the jq key name for the "additional" bag depending on object type. */
export function addlKey(objectType: string): string {
    return objectType === "ev" ? "event_details" : "additional";
}

// ── jq generation ──

function formatConditionValue(value: string): string | number {
    return isNaN(Number(value)) ? JSON.stringify(value) : Number(value);
}

function conditionToJq(condition: Condition): string | null {
    // Use jqPath so numeric segments are bracketed (`.gws[0].time`), matching
    // the body of the generated mapping. Internal storage stays dotted.
    const fieldPath = jqPath(condition.field.split("."));
    const formattedValue = formatConditionValue(condition.value);
    const numericValue = Number(condition.value);

    switch (condition.op) {
        case "not_null": return `${fieldPath} != null`;
        case "is_null": return `${fieldPath} == null`;
        case "equals": return `${fieldPath} == ${formattedValue}`;
        case "not_equals": return `${fieldPath} != ${formattedValue}`;
        case "gt": return `${fieldPath} > ${numericValue}`;
        case "lt": return `${fieldPath} < ${numericValue}`;
        case "contains": return `(${fieldPath} | tostring | contains(${JSON.stringify(condition.value || "")}))`;
        default: return null;
    }
}

/**
 * Generates a complete jq filter string from the builder state.
 * Produces `{ field: .path, ... }` or `if (conditions) then { ... } else empty end`.
 */
export function genJq(
    conditions: Condition[],
    coreFieldMaps: CoreFieldMaps,
    additionalFields: AdditionalField[],
    objectType = "obv",
    literalMaps: Record<string, string> = {},
): string {
    const conditionClauses = conditions
        .filter(c => c.field)
        .map(conditionToJq)
        .filter(Boolean) as string[];

    const outputLines: string[] = [];

    // Literal (hardcoded) values (e.g. event_type, subject_type)
    for (const [key, value] of Object.entries(literalMaps)) {
        if (value.trim()) outputLines.push(`  ${key}: ${JSON.stringify(value)}`);
    }

    // Core field mappings (except location, which is nested)
    const latMapping = coreFieldMaps["location.lat"];
    const lonMapping = coreFieldMaps["location.lon"];
    for (const [key, mapping] of Object.entries(coreFieldMaps)) {
        if (!mapping) continue;
        if (key === "location.lat" || key === "location.lon") continue;
        outputLines.push(`  ${key}: ${jqPath(mapping)}`);
    }

    // Location object (combine lat/lon into nested structure)
    if (latMapping || lonMapping) {
        const lat = latMapping ? jqPath(latMapping) : "null";
        const lon = lonMapping ? jqPath(lonMapping) : "null";
        outputLines.push(`  location: { lat: ${lat}, lon: ${lon} }`);
    }

    // Additional / event_details fields
    const additionalLines = additionalFields
        .filter(f => f.name && f.path)
        .map(f => `    ${JSON.stringify(f.name)}: ${jqPath(f.path.split("."))}`);
    if (additionalLines.length) {
        outputLines.push(`  "${addlKey(objectType)}": {\n${additionalLines.join(",\n")}\n  }`);
    }

    const body = "{\n" + outputLines.join(",\n") + "\n}";
    return conditionClauses.length
        ? `if ( ${conditionClauses.join(" and ")} ) then ${body} else empty end`
        : body;
}

// ── Evaluation (preview) ──

export type EvalResult = { data: any; failedConditions: string[] };

function evaluateCondition(condition: Condition, inputValue: any): boolean {
    const coercedValue = isNaN(Number(condition.value)) ? condition.value : Number(condition.value);

    switch (condition.op) {
        case "not_null": return inputValue != null;
        case "is_null": return inputValue == null;
        case "equals": return inputValue === coercedValue;
        case "not_equals": return inputValue !== coercedValue;
        case "gt": return inputValue > Number(condition.value);
        case "lt": return inputValue < Number(condition.value);
        case "contains": return String(inputValue).includes(condition.value || "");
        default: return true;
    }
}

function formatFailedCondition(condition: Condition): string {
    const valueSuffix = condition.value ? ` ${condition.value}` : "";
    return `${jqPath(condition.field.split("."))} ${condition.op}${valueSuffix}`;
}

/**
 * Evaluates the builder mapping against a sample input object.
 * Returns the transformed output, or a list of failed conditions if the record would be filtered out.
 */
export function evalMapping(
    input: any,
    coreFieldMaps: CoreFieldMaps,
    additionalFields: AdditionalField[],
    conditions: Condition[],
    objectType = "obv",
    literalMaps: Record<string, string> = {},
    complexFields: Record<string, string> = {},
): EvalResult {
    // Check conditions
    const failedConditions: string[] = [];
    for (const condition of conditions) {
        if (!condition.field) continue;
        const fieldValue = valAt(input, condition.field.split("."));
        if (!evaluateCondition(condition, fieldValue)) {
            failedConditions.push(formatFailedCondition(condition));
        }
    }
    if (failedConditions.length > 0) return { data: null, failedConditions };

    // Build output object
    const result: any = {};

    // Literal values (e.g. event_type, subject_type)
    for (const [key, value] of Object.entries(literalMaps)) {
        if (value.trim()) result[key] = value;
    }

    // Complex fields — include as expression placeholder so validation sees them as mapped.
    // Dotted keys (e.g. "location.lat") must be nested so the validator's getNestedValue resolves them.
    for (const [key, expr] of Object.entries(complexFields)) {
        if (key.startsWith("addl:")) continue; // handled below in additional bag
        setAtDotted(result, key, `<${expr}>`);
    }

    const latMapping = coreFieldMaps["location.lat"];
    const lonMapping = coreFieldMaps["location.lon"];
    for (const [key, mapping] of Object.entries(coreFieldMaps)) {
        if (!mapping) continue;
        if (key === "location.lat" || key === "location.lon") continue;
        const val = valAt(input, mapping);
        result[key] = val !== undefined ? val : `<${jqPath(mapping)}>`;
    }
    if (latMapping || lonMapping) {
        const latVal = latMapping ? valAt(input, latMapping) : undefined;
        const lonVal = lonMapping ? valAt(input, lonMapping) : undefined;
        result.location = {
            lat: latVal !== undefined ? latVal : (latMapping ? `<${jqPath(latMapping)}>` : null),
            lon: lonVal !== undefined ? lonVal : (lonMapping ? `<${jqPath(lonMapping)}>` : null),
        };
    }

    const additionalBag: any = {};
    for (const field of additionalFields) {
        if (field.name && field.path) {
            const val = valAt(input, field.path.split("."));
            additionalBag[field.name] = val !== undefined ? val : `<.${field.path}>`;
        }
    }
    // Include complex additional fields as placeholders
    for (const [key, expr] of Object.entries(complexFields)) {
        if (!key.startsWith("addl:")) continue;
        additionalBag[key.slice(5)] = `<${expr}>`;
    }
    if (Object.keys(additionalBag).length) {
        result[addlKey(objectType)] = additionalBag;
    }

    return { data: result, failedConditions: [] };
}

// ── Parser (jq string → builder state) ──

// Matches a jq path expression — covers `.foo.bar`, `.foo[0].bar`, `."weird-key"`.
// Accepts the legacy dot-index form (`.foo.0.bar`) for backward compatibility.
// Matches up to (but not including) whitespace / `|` / `)` delimiters.
//
// NOTE: the character class intentionally over-matches — it accepts `=`, `<`, `>`
// too, so in the comparison regex (`^${PATH_GROUP}\s*(==|!=|>|<)\s*(.+)$`) a
// no-space input like `.foo==5` is first swallowed whole by this greedy group.
// The match only succeeds because the regex engine backtracks the path group far
// enough to let the operator alternation claim `==`. This is harmless for our own
// output (genJq always emits spaces around operators), but keep it in mind before
// narrowing or widening this class.
const PATH_GROUP = String.raw`(\.[^\s|)]+)`;
/** Converts a captured jq path back to the internal dotted form (e.g. `gws[0].time` → `gws.0.time`). */
function jqPathToDotted(jqPathStr: string): string {
    return splitJqPath(jqPathStr).join(".");
}

function parseConditions(conditionString: string): Condition[] {
    const conditions: Condition[] = [];

    for (const part of conditionString.split(" and ")) {
        const trimmed = part.trim();

        const containsMatch = trimmed.match(new RegExp(`^\\(${PATH_GROUP}\\s*\\|\\s*tostring\\s*\\|\\s*contains\\("([^"]*)"\\)\\)$`));
        if (containsMatch) {
            conditions.push({ field: jqPathToDotted(containsMatch[1]), op: "contains", value: containsMatch[2] });
            continue;
        }

        const notNullMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*!=\\s*null$`));
        if (notNullMatch) {
            conditions.push({ field: jqPathToDotted(notNullMatch[1]), op: "not_null", value: "" });
            continue;
        }

        const isNullMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*==\\s*null$`));
        if (isNullMatch) {
            conditions.push({ field: jqPathToDotted(isNullMatch[1]), op: "is_null", value: "" });
            continue;
        }

        const comparisonMatch = trimmed.match(new RegExp(`^${PATH_GROUP}\\s*(==|!=|>|<)\\s*(.+)$`));
        if (comparisonMatch) {
            const operatorMap: Record<string, string> = { "==": "equals", "!=": "not_equals", ">": "gt", "<": "lt" };
            let value = comparisonMatch[3].trim();
            const stringMatch = value.match(/^"([^"]*)"$/);
            if (stringMatch) value = stringMatch[1];
            conditions.push({ field: jqPathToDotted(comparisonMatch[1]), op: operatorMap[comparisonMatch[2]], value });
        }
    }

    return conditions;
}

function parseLiteralMaps(body: string): Record<string, string> {
    const literalMaps: Record<string, string> = {};
    const sanitized = sanitizeJqBody(body);
    const literalRegex = /\b(\w+)\s*:\s*"([^"]*)"/g;
    let match;
    while ((match = literalRegex.exec(sanitized)) !== null) {
        literalMaps[match[1]] = match[2];
    }
    return literalMaps;
}

/** Finds and extracts the additional/event_details block from the body, returning the block content and the body without it. */
function extractAdditionalBlock(body: string): { innerContent: string; bodyWithout: string } | null {
    // Match both quoted and unquoted key: "event_details": { or event_details: {
    const blockStart = body.match(/"?(?:additional|event_details)"?\s*:\s*\{/);
    if (!blockStart || blockStart.index === undefined) return null;

    const openBraceIdx = body.indexOf("{", blockStart.index + blockStart[0].length - 1);
    let braceDepth = 0;
    let closeBraceIdx = -1;
    for (let cursor = openBraceIdx; cursor < body.length; cursor++) {
        if (body[cursor] === "{") braceDepth++;
        else if (body[cursor] === "}") {
            braceDepth--;
            if (braceDepth === 0) { closeBraceIdx = cursor; break; }
        }
    }
    if (closeBraceIdx === -1) return null;

    const innerContent = body.slice(openBraceIdx + 1, closeBraceIdx);
    const bodyWithout = body.slice(0, blockStart.index) + body.slice(closeBraceIdx + 1);
    return { innerContent, bodyWithout };
}

/** Extracts the inner content of a nested `key: { ... }` block via brace matching. */
function extractNestedBlockContent(body: string, key: string): string | null {
    const regex = new RegExp(`\\b${key}\\s*:\\s*\\{`);
    const match = body.match(regex);
    if (!match || match.index === undefined) return null;
    const openBrace = body.indexOf("{", match.index + match[0].length - 1);
    let depth = 0;
    for (let cursor = openBrace; cursor < body.length; cursor++) {
        if (body[cursor] === "{") depth++;
        else if (body[cursor] === "}") {
            depth--;
            if (depth === 0) return body.slice(openBrace + 1, cursor);
        }
    }
    return null;
}

function parseFieldMappings(body: string): CoreFieldMaps {
    const maps: CoreFieldMaps = {};
    const sanitized = sanitizeJqBody(body);

    // Path mappings: `fieldName: .some.path` or `.some[0].path` (including bracket notation and quoted segments)
    const pathRegex = /\b(\w+)\s*:\s*(\.[^\s,})]+)/g;
    let pathMatch;
    while ((pathMatch = pathRegex.exec(sanitized)) !== null) {
        const key = pathMatch[1];
        if (key === "lat" || key === "lon") continue; // handled by location parsing
        maps[key] = splitJqPath(pathMatch[2]);
    }

    // Nested location object: `location: { lat: .x.y.z, lon: .a.b.c }`
    const locationMatch = sanitized.match(/\blocation:\s*\{\s*lat:\s*(\.[^\s,}]+)\s*,\s*lon:\s*(\.[^\s,}]+)\s*\}/);
    if (locationMatch) {
        maps["location.lat"] = splitJqPath(locationMatch[1]);
        maps["location.lon"] = splitJqPath(locationMatch[2]);
    }

    return maps;
}

function parseAdditionalFields(innerContent: string): { addl: AdditionalField[]; complexAddl: Record<string, string> } {
    const addl: AdditionalField[] = [];
    const complexAddl: Record<string, string> = {};
    const sanitized = sanitizeJqBody(innerContent);

    // Match keys with simple path values (including bracket notation and quoted segments)
    const simpleFieldRegex = /\b(\w+)\s*:\s*(\.[^\s,})]+)/g;
    let match;
    const matchedKeys = new Set<string>();
    while ((match = simpleFieldRegex.exec(sanitized)) !== null) {
        addl.push({ name: match[1], path: splitJqPath(match[2]).join(".") });
        matchedKeys.add(match[1]);
    }

    // Detect complex additional fields (keys with expressions that weren't simple path mappings)
    const allKeyRegex = /\b(\w+)\s*:/g;
    let keyMatch;
    while ((keyMatch = allKeyRegex.exec(sanitized)) !== null) {
        const key = keyMatch[1];
        if (matchedKeys.has(key)) continue;
        const afterColon = sanitized.slice(keyMatch.index + keyMatch[0].length);
        const expr = extractExpression(afterColon);
        if (expr) complexAddl[key] = expr.trim();
    }

    return { addl, complexAddl };
}

/** Removes a `select(...)` expression from a string, respecting nested parentheses. */
function stripBalancedSelect(str: string): string {
    const selectIdx = str.indexOf("select(");
    if (selectIdx === -1) return str;
    let depth = 1;
    let closeParen = -1;
    for (let i = selectIdx + 7; i < str.length; i++) {
        if (str[i] === "(") depth++;
        else if (str[i] === ")") {
            depth--;
            if (depth === 0) { closeParen = i; break; }
        }
    }
    if (closeParen === -1) return str;
    return (str.slice(0, selectIdx) + str.slice(closeParen + 1)).trim();
}

/** Checks if the jq string has structural complexity beyond what the builder supports. */
function hasStructuralComplexity(jq: string): boolean {
    if (/\bas\s+\$\w+/.test(jq)) return true;          // variable bindings
    if (/\bdef\s+\w+/.test(jq)) return true;            // function definitions
    if (/\|\s*\.\w+\s*=[^=]/.test(jq)) return true;     // pipe-assignment (.x = expr)

    const beforeBody = jq.replace(/\{[\s\S]*\}\s*$/, "").trim();
    if (!beforeBody.includes("|")) return false;

    // Allow select(...) | as the only preamble (standard condition pattern)
    const withoutSelect = stripBalancedSelect(beforeBody);
    const remainder = withoutSelect.replace(/\s*\|\s*$/, "").trim();
    return remainder.length > 0;
}

/**
 * Extracts all top-level `key: <expression>` assignments from a jq object body.
 * Returns a map of key → raw expression string.
 */
function extractAllFieldKeys(body: string, skipLocationKeys = true): Record<string, string> {
    const fields: Record<string, string> = {};
    const sanitized = sanitizeJqBody(body);
    const keyRegex = /\b(\w+)\s*:/g;
    let match;
    while ((match = keyRegex.exec(sanitized)) !== null) {
        const key = match[1];
        if (skipLocationKeys && (key === "lat" || key === "lon")) continue;
        const afterColon = sanitized.slice(match.index + match[0].length);
        const expr = extractExpression(afterColon);
        if (expr) fields[key] = expr.trim();
    }
    return fields;
}

/** Extracts a balanced expression from the start of a string, respecting nested parens/braces/brackets. */
function extractExpression(str: string): string {
    let depth = 0;
    let i = 0;
    const s = str.trimStart();
    for (; i < s.length; i++) {
        const ch = s[i];
        if (ch === "(" || ch === "{" || ch === "[") depth++;
        else if (ch === ")" || ch === "}" || ch === "]") {
            if (depth === 0) break; // closing brace of parent object
            depth--;
        } else if (ch === "," && depth === 0) break;
        // Skip string literals
        if (ch === '"') {
            i++;
            while (i < s.length && s[i] !== '"') { if (s[i] === "\\") i++; i++; }
        }
    }
    return s.slice(0, i).trim();
}

/** Finds the opening `{` that matches the last `}` in the string, respecting nesting. */
/** Finds the outermost `{ ... }` block by scanning backwards from the last `}` to its matching `{`. */
function findOutputObjectRange(jqBody: string): { start: number; end: number } | null {
    const closingBraceIndex = jqBody.lastIndexOf("}");
    if (closingBraceIndex === -1) return null;

    let braceDepth = 0;
    for (let cursor = closingBraceIndex; cursor >= 0; cursor--) {
        const char = jqBody[cursor];
        if (char === "}") braceDepth++;
        else if (char === "{") {
            braceDepth--;
            if (braceDepth === 0) {
                return { start: cursor, end: closingBraceIndex + 1 };
            }
        }
    }
    return null;
}

/**
 * Parses an existing jq filter string back into builder state.
 * Returns `null` only for empty input. For complex/unparseable jq, returns a result
 * with `isFullyParseable: false` and populated `complexFields`.
 */
export function parseJqToBuilderState(jq: string, literalInputKeys?: Set<string>): ParseResult | null {
    try {
        const trimmed = jq.trim();
        if (!trimmed) return null;

        const structurallyComplex = hasStructuralComplexity(trimmed);

        // Separate condition wrapper from body
        let conditionString = "";
        let body = trimmed;

        // Pattern 1: if (conds) then { ... } else empty end
        const ifMatch = trimmed.match(/^if\s*\(\s*([\s\S]+?)\s*\)\s*then\s*([\s\S]+?)\s*else\s*empty\s*end\s*$/);
        if (ifMatch) {
            conditionString = ifMatch[1];
            body = ifMatch[2].trim();
        }

        // Pattern 2: select(conds) | { ... }
        if (!conditionString && /^\s*select\s*\(/.test(body)) {
            let depth = 1;
            const selectStart = body.indexOf("select(") + 7;
            let closeParen = -1;
            for (let i = selectStart; i < body.length; i++) {
                if (body[i] === "(") depth++;
                else if (body[i] === ")") {
                    depth--;
                    if (depth === 0) { closeParen = i; break; }
                }
            }
            if (closeParen !== -1) {
                const afterSelect = body.slice(closeParen + 1).trim();
                if (afterSelect.startsWith("|")) {
                    conditionString = body.slice(selectStart, closeParen);
                    body = afterSelect.slice(1).trim();
                }
            }
        }

        // Find the outermost output object `{ ... }` by matching the last `}` with its `{`
        const range = findOutputObjectRange(body);
        if (!range) {
            return { maps: {}, conds: [], addl: [], literalMaps: {}, complexFields: {}, isFullyParseable: false, rawJq: trimmed };
        }
        const outputBody = body.slice(range.start, range.end);
        const hasBodyPreamble = range.start > 0;

        const conds = conditionString ? parseConditions(conditionString) : [];

        // Extract additional/event_details block BEFORE parsing core fields,
        // so core field regex doesn't pick up keys from inside the nested block
        const additionalBlock = extractAdditionalBlock(outputBody);
        const coreBody = additionalBlock ? additionalBlock.bodyWithout : outputBody;

        const maps = parseFieldMappings(coreBody);
        const literalMaps = parseLiteralMaps(coreBody);

        // Parse additional fields from the extracted block
        let addl: AdditionalField[] = [];
        const complexFields: Record<string, string> = {};

        if (additionalBlock) {
            const parsedAddl = parseAdditionalFields(additionalBlock.innerContent);
            addl = parsedAddl.addl;
            // Complex additional fields (e.g. gps_time_ms with if/then/else, conversation_id: 0)
            for (const [key, expr] of Object.entries(parsedAddl.complexAddl)) {
                complexFields[`addl:${key}`] = expr;
            }
        }

        // Detect complex core fields: keys present in the core body but not in maps or literalMaps
        const allCoreFields = extractAllFieldKeys(coreBody);
        const parsedKeys = new Set([
            ...Object.keys(maps),
            ...Object.keys(literalMaps),
            "location", "additional", "event_details",
        ]);

        for (const [key, expr] of Object.entries(allCoreFields)) {
            if (parsedKeys.has(key)) continue;
            complexFields[key] = expr;
        }

        // Fallback: if location block exists but lat/lon weren't parsed as simple paths
        // (e.g. $r.location.latitude or (.Latitude | tonumber)), extract them as complex fields
        if (!maps["location.lat"] && !maps["location.lon"]) {
            const locContent = extractNestedBlockContent(sanitizeJqBody(coreBody), "location");
            if (locContent) {
                const locFields = extractAllFieldKeys(locContent, false);
                if (locFields.lat) complexFields["location.lat"] = locFields.lat;
                if (locFields.lon) complexFields["location.lon"] = locFields.lon;
            }
        }

        // literalInput fields that were parsed as path mappings → move to complexFields
        // because genJq would wrap them in quotes, breaking the round-trip
        if (literalInputKeys) {
            for (const key of literalInputKeys) {
                if (maps[key]) {
                    complexFields[key] = jqPath(maps[key]!);
                    delete maps[key];
                }
            }
        }

        const isFullyParseable = !structurallyComplex
            && Object.keys(complexFields).length === 0
            && !hasBodyPreamble;

        return { maps, conds, addl, literalMaps, complexFields, isFullyParseable, rawJq: trimmed };
    } catch {
        return { maps: {}, conds: [], addl: [], literalMaps: {}, complexFields: {}, isFullyParseable: false, rawJq: jq.trim() };
    }
}
